import { useCallback, useMemo, useRef, useState, SyntheticEvent } from 'react';
import { Box, Stack } from '@mui/material';
import { Form } from 'react-final-form';

import { SubBrand } from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Tab, Tabs, TabContext, TabPanel } from '@occc/ui-design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Button, LoadingOverlay, makeZodValidate as makeValidate } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';

import { CONFIGURE_TAB, ConfigureTabForm, MarketingContentForm } from './components';
import { referralProgramConfigurationSchema } from './referral-program-configuration.schema';
import { validateActiveTab } from './referral-program-configuration-tab-validation.utils';
import { ReferralProgramConfigurationValues } from './referral-program-configuration.types';
import { buildMessagingTabs } from './referral-program-configuration.utils';

const validateFull = makeValidate(referralProgramConfigurationSchema);

type SubmitIntent = 'next' | 'publish' | 'none';

type ReferralProgramConfigurationPageProps = {
  initialValues: ReferralProgramConfigurationValues;
  onSubmit(values: ReferralProgramConfigurationValues): Promise<void>;
  onCancel(params: { isDirty: boolean }): void;
  isLoading: boolean;
  isEditable: boolean;
  subBrands: SubBrand[] | null;
};

export const ReferralProgramConfigurationPage = (props: ReferralProgramConfigurationPageProps) => {
  const { initialValues, onSubmit, onCancel, isLoading, isEditable, subBrands } = props;
  const messagingTabs = useMemo(() => buildMessagingTabs(subBrands), [subBrands]);
  const tabOrder = useMemo(
    () => [CONFIGURE_TAB, ...messagingTabs.map((tab) => tab.id)],
    [messagingTabs],
  );
  const [activeTab, setActiveTab] = useState<string>(CONFIGURE_TAB);
  const submitIntentRef = useRef<SubmitIntent>('none');

  const handleTabChange = (_event: SyntheticEvent, value: string) => {
    submitIntentRef.current = 'none';
    setActiveTab(value);
  };

  const isLastTab = activeTab === tabOrder[tabOrder.length - 1];

  const validateForm = useCallback(
    (values: ReferralProgramConfigurationValues) => {
      if (submitIntentRef.current === 'next') {
        return validateActiveTab(values, activeTab, messagingTabs);
      }

      return validateFull(values);
    },
    [activeTab, messagingTabs],
  );

  const handleFormSubmit = useCallback(
    async (values: ReferralProgramConfigurationValues) => {
      if (submitIntentRef.current !== 'publish') {
        return;
      }

      await onSubmit(values);
    },
    [onSubmit],
  );

  const handleNextTab = useCallback(
    async (
      values: ReferralProgramConfigurationValues,
      handleSubmit: () => Promise<Record<string, unknown> | undefined> | undefined,
    ) => {
      const tabErrors = await validateActiveTab(values, activeTab, messagingTabs);

      if (Object.keys(tabErrors).length > 0) {
        submitIntentRef.current = 'next';

        try {
          await handleSubmit();
        } finally {
          submitIntentRef.current = 'none';
        }

        return;
      }

      const nextTab = tabOrder[tabOrder.indexOf(activeTab) + 1];

      if (nextTab) {
        setActiveTab(nextTab);
      }
    },
    [activeTab, messagingTabs, tabOrder],
  );

  return (
    <Form<ReferralProgramConfigurationValues>
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validate={validateForm}
      render={({ dirty, handleSubmit, values }) => (
        <CanEditFormFields isEditable={isEditable}>
          <Stack
            component='form'
            noValidate
            onSubmit={async (event) => {
              event.preventDefault();

              if (submitIntentRef.current !== 'publish') {
                return;
              }

              try {
                await handleSubmit(event);
              } finally {
                submitIntentRef.current = 'none';
              }
            }}
          >
            <TabContext value={activeTab}>
              <Tabs color='secondary' value={activeTab} onChange={handleTabChange}>
                <Tab value={CONFIGURE_TAB} label='Configure' />
                {messagingTabs.map((tab) => (
                  <Tab key={tab.id} value={tab.id} label={tab.label} disabled={isLoading} />
                ))}
              </Tabs>

              <Box position='relative'>
                <TabPanel value={CONFIGURE_TAB} sx={{ p: 0 }}>
                  <ConfigureTabForm />
                </TabPanel>
                {messagingTabs.map((tab) => (
                  <TabPanel key={tab.id} value={tab.id} sx={{ p: 0 }}>
                    <MarketingContentForm fieldNamePrefix={`marketingContents.${tab.index}`} />
                  </TabPanel>
                ))}
                {isLoading && <LoadingOverlay rounded />}
              </Box>
            </TabContext>

            {isEditable && (
              <Stack direction='row' justifyContent='flex-end' spacing={1} mt={3}>
                <Button
                  variant='outlined'
                  type='button'
                  onClick={() => onCancel({ isDirty: dirty })}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                {!isLastTab ? (
                  <Button
                    variant='contained'
                    type='button'
                    onClick={() => handleNextTab(values, handleSubmit)}
                    disabled={isLoading}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant='contained'
                    type='submit'
                    onClick={() => {
                      submitIntentRef.current = 'publish';
                    }}
                    disabled={isLoading}
                  >
                    Publish
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </CanEditFormFields>
      )}
    />
  );
};
